import React from 'react';
import { FormCheck } from 'react-bootstrap';

import { useFilter } from '../../hooks';
import { EventsFilter } from '../../interfaces';
import { SavedSearchService } from '../../services/savedSearch.service';
import { Debounce } from '../../utils/debounce';
import { arrayToggle } from '../../utils/arrayToggle';
import { ToggleButton as BaseToggleButton } from '../atoms/toggleButton';
import { updateFilterPartial } from '../../queries';

interface Props {
  field: keyof EventsFilter;
  value: any;
  children: any;
  className?: string;
}

interface TogglableElementProps extends Props {
  field:
    | 'route_ids'
    | 'device_ids'
    | 'vehicle_ids'
    | 'selectedFilters'
    | 'vehicle_types'
    | 'active_lanes'
    | 'license_plates'
    | keyof EventsFilter;
}

const debouncer = new Debounce(500);

export function FilterRadioOption(props: Props) {
  const filter = useFilter();

  const getCheckboxInputProps = () => {
    const { field, value, children } = props;
    const checked = filter[field] === value;
    const id = field + value;
    const label = children;
    return { value, checked, id, label, onChange: setStatusFilter };
  };

  const setStatusFilter = (e: any) => {
    const { field, value } = props;
    updateFilterPartial({ [field]: value });
    debouncer.debounce(() => {
      SavedSearchService.saveSearch(filter);
    });
  };

  return <FormCheck type="radio" {...getCheckboxInputProps()} className="hdn-radio-button" />;
}

export function ClearLink(props: Props) {
  const filter = useFilter();

  const setStatusFilter = (e: any) => {
    let { value, field } = props;

    if (filter[field] === value) {
      value = undefined; // clear the value when clicking the same button again
    }

    e.preventDefault();
    e.stopPropagation();
    updateFilterPartial({ [field]: value });

    debouncer.debounce(() => {
      SavedSearchService.saveSearch(filter);
    });
  };

  return (
    <span {...props} onClick={setStatusFilter}>
      {props.children}
    </span>
  );
}

export function ToggleButton(props: TogglableElementProps) {
  const { value, field } = props;

  const filter = useFilter();
  const selectedFilters = filter[field] ?? [];

  const isEnabled = selectedFilters.includes(value);

  const toggleFilter = (identifier: string) => {
    const newSelectedFilters = arrayToggle(selectedFilters, identifier);

    updateFilterPartial({ [field]: newSelectedFilters });

    debouncer.debounce(() => {
      SavedSearchService.saveSearch(filter);
    });
  };

  return (
    <BaseToggleButton role="radio" aria-checked={isEnabled} {...props} onClick={() => toggleFilter(value)} isEnabled={isEnabled}>
      {props.children}
    </BaseToggleButton>
  );
}
